import {useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExternalLink} from "@fortawesome/free-solid-svg-icons";

import Accordion from "./Accordion.js";
import {prettyDateInUserTimezone} from "../utils/helpers.js";
import {buildChecksheetRecordUrl} from "../utils/checksheetsDeepLink.js";

import {pad} from "../style/components/variables.js";
import {Button, Text} from "../style/components/general.js";

// Component Service State: whether an individual checksheet component is enabled/disabled (in/out of service).

export const stateLabel = (disabled, enabledLabel, disabledLabel) =>
  disabled ? disabledLabel || "Out of Service" : enabledLabel || "In Service";

export const getComponentServiceState = attributes =>
  attributes?.state ?? attributes?.stateHistory?.[0];

export const formatStateHistorySummary = (
  {disabled, userLabel, time},
  {enabledLabel, disabledLabel, timezone}
) => {
  const label = stateLabel(disabled, enabledLabel, disabledLabel);
  let summary = `Set to ${label}`;
  if (userLabel) summary += ` by ${userLabel}`;
  if (time) summary += ` at ${prettyDateInUserTimezone(time, timezone, "MMM D YYYY, h:mm A")}`;
  return summary;
};

const ComponentServiceStateHistory = ({
  stateHistory,
  enabledLabel,
  disabledLabel,
  facilitySlug,
  checksheetId,
  timezone,
  initialVisible = 1,
  variant = "inline",
  emptyCentered = false
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!stateHistory?.length) {
    return emptyCentered ? (
      <Text center quiet>
        No History
      </Text>
    ) : (
      <Text quiet small>
        No history
      </Text>
    );
  }

  const renderEntryBody = ({explanation, userLabel, time, disabled, recordId}) => {
    const summaryText = formatStateHistorySummary(
      {disabled, userLabel, time},
      {enabledLabel, disabledLabel, timezone}
    );

    const summary = (
      <>
        Set to{" "}
        <StateLabel disabled={disabled}>
          {stateLabel(disabled, enabledLabel, disabledLabel)}
        </StateLabel>
        {userLabel ? <> by {userLabel}</> : null} at{" "}
        {prettyDateInUserTimezone(time, timezone, "MMM D YYYY, h:mm A")}
      </>
    );

    if (variant === "accordion") {
      return (
        <Accordion
          key={`entry-${time}`}
          wrap
          label={
            <AccordionEntryLabel title={summaryText}>
              <EntrySummary>{summary}</EntrySummary>
              <StateHistoryRecordLink
                entry={{recordId, time}}
                facilitySlug={facilitySlug}
                checksheetId={checksheetId}
                timezone={timezone}
              />
            </AccordionEntryLabel>
          }>
          {explanation ? (
            <AccordionExplanation>
              <Text quiet>
                <strong>Explanation:</strong> {explanation}
              </Text>
            </AccordionExplanation>
          ) : null}
        </Accordion>
      );
    }

    return (
      <HistoryEntry key={`${time}-${userLabel}-${disabled}`}>
        <InlineEntryRow>
          <Text small title={summaryText}>
            {summary}
          </Text>
          <StateHistoryRecordLink
            entry={{recordId, time}}
            facilitySlug={facilitySlug}
            checksheetId={checksheetId}
            timezone={timezone}
          />
        </InlineEntryRow>
        {explanation && (
          <Explanation quiet small>
            <strong>Explanation:</strong> {explanation}
          </Explanation>
        )}
      </HistoryEntry>
    );
  };

  if (variant === "accordion") {
    return <>{stateHistory.map(renderEntryBody)}</>;
  }

  const visibleCount = expanded
    ? stateHistory.length
    : Math.min(initialVisible, stateHistory.length);
  const hiddenCount = stateHistory.length - initialVisible;
  const entries = stateHistory.slice(0, visibleCount);

  return (
    <HistoryList>
      {entries.map(renderEntryBody)}
      {!expanded && hiddenCount > 0 && (
        <ShowMore type="button" onClick={() => setExpanded(true)}>
          Show more ({hiddenCount})
        </ShowMore>
      )}
    </HistoryList>
  );
};

ComponentServiceStateHistory.propTypes = {
  stateHistory: PropTypes.arrayOf(PropTypes.object),
  enabledLabel: PropTypes.string,
  disabledLabel: PropTypes.string,
  facilitySlug: PropTypes.string,
  checksheetId: PropTypes.number,
  timezone: PropTypes.string,
  initialVisible: PropTypes.number,
  variant: PropTypes.oneOf(["inline", "accordion"]),
  emptyCentered: PropTypes.bool
};

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pad / 2}px;
  margin-top: ${pad / 2}px;
`;

const HistoryEntry = styled.div`
  padding-left: ${pad}px;
`;

const AccordionEntryLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pad}px;
  width: 100%;
  min-width: 0;
`;

const EntrySummary = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const InlineEntryRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pad / 2}px;
`;

const AccordionExplanation = styled.div`
  margin: ${pad / 2}px 0 ${pad / 2}px ${pad * 2}px;
`;

const StateLabel = styled.span`
  font-weight: bold;
  color: ${({theme, disabled}) => (disabled ? theme.error : theme.success)};
`;

const Explanation = styled(Text)`
  margin-top: ${pad / 4}px;
  padding-left: ${pad}px;
`;

export const StateHistoryRecordLink = ({entry, facilitySlug, checksheetId}) => {
  const {recordId} = entry || {};
  const href =
    recordId != null && facilitySlug
      ? buildChecksheetRecordUrl(facilitySlug, {checksheetId, recordId})
      : null;

  if (!href) return null;

  return (
    <RecordLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="View record"
      aria-label="View record"
      onClick={e => e.stopPropagation()}>
      <FontAwesomeIcon icon={faExternalLink} />
    </RecordLink>
  );
};

StateHistoryRecordLink.propTypes = {
  entry: PropTypes.object,
  facilitySlug: PropTypes.string,
  checksheetId: PropTypes.number
};

const RecordLink = styled.a`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  padding: ${pad / 4}px;
  color: ${({theme}) => theme.secondary};
  text-decoration: none;

  &:hover {
    color: ${({theme}) => theme.primary};
  }

  svg {
    width: 0.9em;
    height: 0.9em;
  }
`;

const ShowMore = styled(Button)`
  align-self: flex-start;
  margin-top: ${pad / 4}px;
  padding: ${pad / 4}px ${pad / 2}px;
`;

export default ComponentServiceStateHistory;
